import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_TOKEN } from 'constants/constants';
import { serializeQueryParams } from 'utils/helpers';

export interface IPartType {
  id: number;
  typeKey: string;
  elementType: string;
  constructionType: string;
  imageUrl: string;
  displayText: string;
}

export interface IPartTypesResponse {
  data: IPartType[];
  count: number;
}

export interface IParts {
  id: number;
  name: string;
  path: string;
  projectFolder: string | null;
  intendedColor: string | null;
  intendedMaterial: string | null;
  partType: IPartType;
  height: number | null;
  isLocked: boolean;
  customProps: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  machineName: string | null;
  logo: string | null;
  status: string | null;
  lotNumber: string | null;
  millId: number | null;
}

export interface IStockMaterial {
  id: number;
  name: string;
  guid: string;
  storageLocation: string | null;
  blankType: string | null;
  lotNumber: string;
  material: string | null;
  created_at: string;
  updated_at: string;
  externalId: string | null;
  millId: number | null;
}

export interface IStockMaterialPart {
  id: number;
  part: IParts;
  stockMaterial: IStockMaterial;
  status: string | null;
  isPreparedAutomatically: boolean;
  userInfo: {
    pcName?: string;
    userName?: string;
  };
  processId: string;
  failedReason: string | null;
  created_at: string;
  updated_at: string;
  machineId: string | null;
  linkRefGuid: string | null;
}

export interface IStockMaterialPartsListResponse {
  count: number;
  data: IStockMaterialPart[];
}

export interface IPartsListRequest {
  pageSize: number;
  pageNumber: number;
  sort?: string;
  search?: string;
  isPrepared?: boolean | null;
  isLocked?: boolean | null;
  partType?: string | null;
  millId?: number | null;
  [key: string]: any;
}

export interface IPartsListResponse {
  count: number;
  data: IParts[];
}

export const partsApi = createApi({
  reducerPath: 'partsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers) => {
      const authToken = localStorage.getItem(API_TOKEN);
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Parts', 'StockMaterialPart', 'PartTypes'],
  endpoints: (builder) => ({
    getParts: builder.query<IPartsListResponse, IPartsListRequest>({
      query: ({ pageSize = 20, pageNumber = 1, sort, ...rest }) => ({
        url: `/v1/parts?${serializeQueryParams({
          pageSize,
          pageNumber,
          ...(sort ? { sort } : {}),
          ...rest,
        })}`,
        method: 'GET',
      }),
      providesTags: ['Parts'],
    }),
    redoPart: builder.mutation<void, { id: number; millId: number | null }>({
      query: ({ id, millId }) => ({
        url: `/v1/parts/${id}/redo`,
        method: 'POST',
        body: { millId },
      }),
      invalidatesTags: ['Parts'],
    }),
    getStockMaterialPart: builder.query<
      IStockMaterialPartsListResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/v1/stock-material-parts?partId=${id}`,
        method: 'GET',
      }),
      providesTags: ['StockMaterialPart'],
    }),
    getPartTypes: builder.query<IPartTypesResponse, void>({
      query: () => ({
        url: `/v1/part-types`,
        method: 'GET',
      }),
      providesTags: ['PartTypes'],
    }),
  }),
});

export const {
  useGetPartsQuery,
  useRedoPartMutation,
  useGetStockMaterialPartQuery,
  useGetPartTypesQuery,
} = partsApi;
